<template>
  <div class="w-full bg-gray-50 h-screen flex flex-col">
    <!-- Header -->
    <div class="bg-white border-b px-4 lg:px-6 py-3 lg:py-4">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div class="flex items-center gap-4">
          <button @click="goBack" class="text-gray-600 hover:text-gray-800 text-sm lg:text-base">
            <font-awesome-icon icon="arrow-left" class="mr-2" />
            <span class="hidden sm:inline">Zurück zur Tabelle</span>
            <span class="sm:hidden">Zurück</span>
          </button>
          <h1 class="text-lg lg:text-2xl font-bold">
            {{ headerTitle }}
          </h1>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button
            v-if="showProjectActions"
            @click="copyProject"
            class="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 text-sm lg:text-base"
          >
            <span class="hidden sm:inline">Projekt kopieren</span>
            <span class="sm:hidden">Kopieren</span>
          </button>
          <button
            v-if="showProjectActions"
            @click="openMergeModal"
            class="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 text-sm lg:text-base"
          >
            <span class="hidden sm:inline">Projekt zusammenführen</span>
            <span class="sm:hidden">Zusammenführen</span>
          </button>
          <button
            v-if="showSaveButtons"
            @click="saveProject"
            :disabled="saving"
            class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm lg:text-base"
          >
            {{ saving ? 'Speichert...' : 'Speichern' }}
          </button>
          <button
            v-if="showSaveButtons"
            @click="saveAndClose"
            :disabled="saving"
            class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm lg:text-base"
          >
            <span class="hidden sm:inline">Speichern und schließen</span>
            <span class="sm:hidden">Speichern & Schließen</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 overflow-auto p-4 lg:p-6">
      <div class="max-w-7xl mx-auto flex flex-col lg:flex-row gap-4 lg:gap-6">
        <!-- Left table of contents (desktop only) -->
        <nav class="hidden lg:block lg:w-56 xl:w-64 flex-shrink-0" aria-label="Projektnavigation">
          <div class="sticky top-4 max-h-[calc(100vh-10rem)] overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
            <div class="flex items-center justify-between px-3 pb-2 pt-1">
              <div>
                <div class="text-[11px] font-bold uppercase tracking-wider text-gray-400">Projekt</div>
                <div class="mt-0.5 text-sm font-semibold text-gray-800">Inhalt</div>
              </div>
              <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">{{ navSections.length }}</span>
            </div>

            <div class="space-y-0.5 border-t border-gray-100 pt-2">
              <button
                v-for="item in navSections"
                :key="item.id"
                @click="scrollToSection(item.id)"
                class="group flex w-full items-center gap-3 !border-0 !bg-transparent !px-3 !py-2 text-left text-sm !font-medium !shadow-none transition-colors duration-150 !text-gray-600 hover:!bg-gray-50 hover:!text-gray-900"
              >
                <span class="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-400 transition-colors group-hover:bg-gray-200 group-hover:text-gray-600">
                  <font-awesome-icon v-if="item.icon" :icon="item.icon" class="text-xs" />
                </span>
                <span class="min-w-0 leading-5">{{ item.label }}</span>
              </button>
            </div>
          </div>
        </nav>
        <div class="flex-1 min-w-0 space-y-4 lg:space-y-6">
        <div ref="section-projekt" class="bg-white rounded-lg shadow p-4 lg:p-6 space-y-4">
          <div class="flex items-center justify-between">
            <h2 class="flex-1 text-lg lg:text-xl font-semibold cursor-pointer select-none flex items-center justify-between" @click="toggleSection('projekt')">
              <span>Projekt</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.projekt }" />
            </h2>
          </div>
          <div v-show="!collapsedSections.projekt" class="space-y-4">

          <div
            v-if="projectId && !copyMode && projectStats"
            class="bg-yellow-100 text-yellow-900 p-4 rounded-lg text-sm font-semibold"
          >
            Zu diesem Vertrag gehören aktuell
            <a href="#" class="underline" @click.prevent="$router.push(contractsUrl)">
              {{ projectStats.numberContracts }}
            </a>
            Verträge (Bestätigt: {{ projectStats.numberContractsConfirmed }}
            <template v-if="project.Tagesauswahl">
              , Montag: {{ projectStats.Montag || 0 }}, Dienstag: {{ projectStats.Dienstag || 0 }}, Mittwoch: {{
                projectStats.Mittwoch || 0
              }}, Donnerstag: {{ projectStats.Donnerstag || 0 }}, Freitag: {{ projectStats.Freitag || 0 }}
            </template>
            ).
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">ID</label>
              <div class="p-2 bg-gray-50 rounded">
                {{ projectId || 'Wird beim Speichern erzeugt' }}
              </div>
            </div>
            <div v-for="field in baseFields" :key="field.key" v-memo="[project[field.key]]" :class="field.colSpan || ''">
              <div v-if="field.type === 'checkbox'" class="flex items-center gap-2 pt-6">
                <input
                  :id="field.key"
                  v-model="project[field.key]"
                  type="checkbox"
                  class="small"
                />
                <label :for="field.key" class="text-sm font-medium text-gray-700">
                  {{ field.label }}
                </label>
              </div>
              <template v-else>
                <label class="block text-sm font-medium text-gray-700 mb-1" :for="field.key">
                  {{ field.label }}
                </label>
                <select
                  v-if="field.type === 'select'"
                  :id="field.key"
                  v-model="project[field.key]"
                  class="w-full border rounded px-3 py-2"
                >
                  <option v-for="option in field.options" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
                <input
                  v-else
                  :id="field.key"
                  v-model="project[field.key]"
                  :type="field.type"
                  :placeholder="field.placeholder"
                  class="w-full border rounded px-3 py-2"
                />
              </template>
              <p v-if="field.help" class="text-xs text-gray-500 mt-1">{{ field.help }}</p>
            </div>
          </div>
          </div>
        </div>

        <div v-for="section in sections" :key="section.id" :ref="'section-' + section.id" class="bg-white rounded-lg shadow p-4 lg:p-6">
          <h3 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection(section.id)">
            <span>{{ section.title }}</span>
            <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections[section.id] }" />
          </h3>
          <div v-show="!collapsedSections[section.id]">

          <!-- Special rendering for sondertage section -->
          <div v-if="section.id === 'sondertage' && projectId && !copyMode">
            <div class="bg-blue-50 border border-blue-200 rounded p-3 mb-4 flex flex-col sm:flex-row sm:items-center gap-2">
              <span class="text-sm text-blue-800 font-medium mr-1">Link:</span>
              <input :value="sondertageLink" readonly class="flex-1 font-mono text-sm border border-blue-300 rounded px-2 py-1 bg-white" />
              <button @click="copySondertageLink" class="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 shrink-0">
                {{ sondertagecopied ? 'Kopiert!' : 'Kopieren' }}
              </button>
            </div>

            <div v-if="sondertage.length === 0" class="text-gray-500 text-sm py-2">Keine Sondertage konfiguriert.</div>

            <div v-for="(st, i) in sondertage" :key="st.id || 'new-'+i" class="border rounded p-4 mb-3" :class="{ 'bg-green-50 border-green-200': st.saved }">
              <div class="flex items-center justify-between mb-2">
                <span v-if="st.saved" class="text-xs text-green-600 font-medium">✓ Gespeichert</span>
                <span v-else class="text-xs text-gray-400">Nicht gespeichert</span>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Bezeichnung</label>
                  <input v-model="st.label" placeholder="z.B. Ferienbetreuung" class="w-full border rounded px-2 py-1 text-sm" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Startdatum</label>
                  <input v-model="st.startDate" type="date" class="w-full border rounded px-2 py-1 text-sm" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Enddatum</label>
                  <input v-model="st.endDate" type="date" class="w-full border rounded px-2 py-1 text-sm" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Standard (Voreinstellung)</label>
                  <select v-model="st.default" class="w-full border rounded px-2 py-1 text-sm">
                    <option value="abwesend">abwesend</option>
                    <option value="anwesend">anwesend</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Bearbeitungsstart</label>
                  <input v-model="st.editStart" type="date" class="w-full border rounded px-2 py-1 text-sm" />
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Bearbeitungsende</label>
                  <input v-model="st.editEnd" type="date" class="w-full border rounded px-2 py-1 text-sm" />
                </div>
                <div class="flex items-end">
                  <button @click="saveSondertag(st, i)" class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">Speichern</button>
                </div>
                <div class="flex items-end">
                  <button @click="removeSondertag(st, i)" class="text-red-500 hover:text-red-700 text-sm font-bold px-2 py-1 border rounded hover:bg-red-50">Löschen</button>
                </div>
              </div>
            </div>

            <button @click="addSondertag" class="mt-3 px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm">+ Hinzufügen</button>
          </div>
          <!-- End special rendering for sondertage section -->

          <div v-if="section.id === 'templates'" class="bg-yellow-50 text-yellow-900 p-4 rounded-lg text-sm font-semibold mb-4">
            Mögliche Platzhalter für Templates: %KOSTEN%, %LEISTUNG%, %VORNAME%, %NACHNAME%, %ENDDATUM%,
            %BETREUUNGSENDE%, %KUENDIGUNGSDATUM%, %SEPAMANDAT%, %START%, %ENDE%, %TAGE%, %WOCHENTAGE%,
            %ANZAHLTAGE%
          </div>
          <div v-if="section.id !== 'sondertage'" :class="['grid gap-4', section.grid]">
            <div v-for="field in section.fields" :key="field.key" v-memo="[project[field.key]]" :class="field.colSpan || ''">
              <div v-if="field.type === 'checkbox'" class="flex items-center gap-2 pt-4">
                <input
                  :id="field.key"
                  v-model="project[field.key]"
                  type="checkbox"
                  class="small"
                />
                <label :for="field.key" class="text-sm font-medium text-gray-700">
                  {{ field.label }}
                </label>
              </div>
              <template v-else>
                <label class="block text-sm font-medium text-gray-700 mb-1" :for="field.key">
                  {{ field.label }}
                </label>
                <select
                  v-if="field.type === 'select'"
                  :id="field.key"
                  v-model="project[field.key]"
                  class="w-full border rounded px-3 py-2"
                >
                  <option v-for="option in field.options" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
                <textarea
                  v-else-if="field.type === 'textarea'"
                  :id="field.key"
                  v-model="project[field.key]"
                  :rows="field.rows || 4"
                  :placeholder="field.placeholder"
                  autocomplete="off"
                  class="w-full border rounded px-3 py-2 resize-y"
                ></textarea>
                <template v-else-if="field.type === 'betreuungsende'">
                  <div class="flex flex-wrap gap-2 mb-2 min-h-8">
                    <span
                      v-for="(time, i) in betreuungsendePills"
                      :key="i"
                      class="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-sm px-2 py-1 rounded-full"
                    >
                      {{ time }}
                      <button type="button" @click="removeBetreuungsende(i)" class="text-blue-600 hover:text-blue-900 font-bold leading-none ml-1">&times;</button>
                    </span>
                  </div>
                  <div class="flex gap-2">
                    <input
                      v-model="betreuungsendeInput"
                      type="text"
                      placeholder="HH:MM hinzufügen"
                      class="border rounded px-3 py-2 text-sm w-36"
                      @keydown.enter.prevent="addBetreuungsende"
                    />
                    <button type="button" @click="addBetreuungsende" class="px-3 py-2 border rounded text-sm hover:bg-gray-50">+</button>
                  </div>
                </template>
                <input
                  v-else
                  :id="field.key"
                  v-model="project[field.key]"
                  :type="field.type"
                  :placeholder="field.placeholder"
                  class="w-full border rounded px-3 py-2"
                />
              </template>
              <p v-if="field.help" class="text-xs text-gray-500 mt-1">{{ field.help }}</p>
            </div>
          </div>
          </div>
        </div>

        <div v-if="projectId && !copyMode" ref="section-dokumente" class="bg-white rounded-lg shadow p-4 lg:p-6">
          <div class="flex flex-wrap items-center justify-between gap-2 mb-4">
            <h2 class="flex-1 text-lg lg:text-xl font-semibold cursor-pointer select-none flex items-center justify-between" @click="toggleSection('dokumente')">
              <span>Dokumente</span>
              <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.dokumente }" />
            </h2>
            <div class="flex gap-2">
              <button
                v-if="canUploadFiles"
                @click="uploadDocument"
                class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Dokument hochladen
              </button>
              <button
                @click="refreshSupplemental"
                class="px-4 py-2 border rounded hover:bg-gray-50"
              >
                Aktualisieren
              </button>
            </div>
          </div>
          <div class="overflow-x-auto" v-show="!collapsedSections.dokumente">
            <table class="w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left px-3 py-2">Name</th>
                  <th class="text-left px-3 py-2">Typ</th>
                  <th class="text-left px-3 py-2">Hochgeladen am</th>
                  <th class="text-left px-3 py-2">Aktion</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="files.length === 0">
                  <td colspan="4" class="text-center text-gray-500 py-6">Keine Dokumente vorhanden</td>
                </tr>
                <tr v-for="file in files" :key="file.id" class="border-t">
                  <td class="px-3 py-2">{{ file.originalName }}</td>
                  <td class="px-3 py-2">{{ file.kind }}</td>
                  <td class="px-3 py-2">{{ formatDateTime(file.uploadedAt) }}</td>
                  <td class="px-3 py-2">
                    <button
                      type="button"
                      class="text-blue-600 hover:text-blue-800 mr-2"
                      @click="openFile(file)"
                    >
                      Ansehen
                    </button>
                    <button
                      v-if="canUploadFiles"
                      type="button"
                      class="text-red-600 hover:text-red-800"
                      @click="deleteFile(file)"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
                <tr v-if="canUploadFiles" class="border-t">
                  <td class="px-3 py-2">
                    <button type="button" class="text-blue-600 hover:text-blue-800" @click="uploadDocument">
                      Hochladen
                    </button>
                  </td>
                  <td class="px-3 py-2">
                    <button type="button" class="text-blue-600 hover:text-blue-800" @click="uploadDocument">
                      Hochladen
                    </button>
                  </td>
                  <td class="px-3 py-2">
                    <button type="button" class="text-blue-600 hover:text-blue-800" @click="uploadDocument">
                      Hochladen
                    </button>
                  </td>
                  <td class="px-3 py-2">
                    <button type="button" class="text-blue-600 hover:text-blue-800" @click="uploadDocument">
                      Hochladen
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="projectId && !copyMode && project.EhrenamtsSelfservice" ref="section-ehrenamt" class="bg-white rounded-lg shadow p-4 lg:p-6">
          <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('ehrenamt')">
            <span>Ehrenamt Teilnahmeanfragen</span>
            <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.ehrenamt }" />
          </h2>
          <div class="overflow-x-auto" v-show="!collapsedSections.ehrenamt">
            <table class="w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left px-3 py-2">Name</th>
                  <th class="text-left px-3 py-2">Status</th>
                  <th class="text-left px-3 py-2">Vergütung</th>
                  <th class="text-left px-3 py-2">Aktion</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="participationRequests.length === 0">
                  <td colspan="4" class="text-center text-gray-500 py-6">Keine Anfragen vorhanden</td>
                </tr>
                <tr v-for="req in participationRequests" :key="req.id" class="border-t">
                  <td class="px-3 py-2">
                    <router-link :to="`/customers/${req.userId}`" class="text-blue-600 hover:underline font-medium">{{ req.userName }}</router-link>
                  </td>
                  <td class="px-3 py-2">
                    <select v-model="req.status" class="border rounded px-2 py-1">
                      <option value="Angefragt">Angefragt</option>
                      <option value="Abgelehnt">Abgelehnt</option>
                      <option value="Bestätigt">Bestätigt</option>
                      <option value="Bezahlt">Bezahlt</option>
                    </select>
                  </td>
                  <td class="px-3 py-2">
                    <input v-model="req.compensation" class="border rounded px-2 py-1 w-full" placeholder="z.B. 15 €/h" />
                  </td>
                  <td class="px-3 py-2">
                    <div class="flex gap-1">
                      <button v-if="req.status === 'Bestätigt'" class="px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-xs" @click="saveParticipationStatus(req, false)">Bestätigen (mit Mail)</button>
                      <button v-if="req.status === 'Bestätigt'" class="px-2 py-1 bg-green-700 text-white rounded hover:bg-green-800 text-xs" @click="saveParticipationStatus(req, true)">Bestätigen (Mail bearbeiten)</button>
                      <button v-else class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700" @click="saveParticipationStatus(req, false)">Speichern</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="projectId && !copyMode && canViewPermissions" ref="section-berechtigungen" class="bg-white rounded-lg shadow p-4 lg:p-6">
          <h2 class="text-lg lg:text-xl font-semibold mb-4 cursor-pointer select-none flex items-center justify-between" @click="toggleSection('berechtigungen')">
            <span>Berechtigungen</span>
            <font-awesome-icon icon="chevron-down" class="text-base text-gray-500 transition-transform duration-200" :class="{ 'rotate-180': !collapsedSections.berechtigungen }" />
          </h2>
          <div v-show="!collapsedSections.berechtigungen">
          <div class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="bg-gray-50">
                <tr>
                  <th class="text-left px-3 py-2">E-Mail</th>
                  <th class="text-left px-3 py-2">Berechtigungen</th>
                  <th class="text-left px-3 py-2">Aktion</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="view in restrictedViews" :key="view.id" class="border-t">
                  <td class="px-3 py-2">
                    <select v-model="view.userId" class="w-full border rounded px-2 py-1">
                      <option value="">Mailadresse auswählen</option>
                      <option v-for="user in availableUsers" :key="user.id" :value="user.id">
                        {{ user.mail }}
                      </option>
                    </select>
                  </td>
                  <td class="px-3 py-2">
                    <select v-model.number="view.permission" class="w-full border rounded px-2 py-1">
                      <option v-for="option in permissionOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                      </option>
                    </select>
                  </td>
                  <td class="px-3 py-2">
                    <button
                      type="button"
                      class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      @click="savePermission(view)"
                    >
                      Speichern
                    </button>
                  </td>
                </tr>
                <tr class="border-t">
                  <td class="px-3 py-2">
                    <select v-model="newPermission.userId" class="w-full border rounded px-2 py-1">
                      <option value="">Mailadresse auswählen</option>
                      <option v-for="user in availableUsers" :key="user.id" :value="user.id">
                        {{ user.mail }}
                      </option>
                    </select>
                  </td>
                  <td class="px-3 py-2">
                    <select v-model.number="newPermission.permission" class="w-full border rounded px-2 py-1">
                      <option v-for="option in permissionOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                      </option>
                    </select>
                  </td>
                  <td class="px-3 py-2">
                    <button
                      type="button"
                      class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
                      @click="savePermission(newPermission, true)"
                    >
                      Speichern
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="!canEditPermissions" class="text-xs text-gray-500 mt-2">
            Für Änderungen an den Berechtigungen benötigen Sie Zugriff auf das Berechtigungsmodul.
          </p>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Floating Quick Navigation (mobile only) -->
    <div class="fixed bottom-4 right-4 z-40 lg:hidden">
      <div v-if="quickNavOpen" class="absolute bottom-14 right-0 bg-white rounded-lg shadow-xl border w-72 max-h-[70vh] overflow-y-auto">
        <div class="py-2">
          <button v-for="item in navSections" :key="item.id" @click="scrollToSection(item.id)" class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100">{{ item.label }}</button>
        </div>
      </div>
      <button @click="quickNavOpen = !quickNavOpen" class="bg-blue-600 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-lg">
        <font-awesome-icon :icon="quickNavOpen ? 'times' : 'compass'" />
      </button>
    </div>

    <!-- Merge Modal -->
    <div v-if="mergeModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.self="closeMergeModal">
      <div class="bg-white rounded-lg p-4 lg:p-6 w-full max-w-lg space-y-4 mx-4">
        <h2 class="text-lg lg:text-xl font-semibold">Projekte zusammenführen</h2>
        <p class="text-sm text-gray-600">
          Alle Verträge des aktuellen Projekts werden in das Zielprojekt verschoben. Das aktuelle Projekt wird anschließend archiviert.
        </p>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Quell-Projekt</label>
          <div class="p-2 bg-gray-50 rounded">{{ project.Titel }} (ID: {{ projectId }})</div>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Ziel-Projekt</label>
          <select v-model="mergeTargetId" class="w-full border rounded px-3 py-2">
            <option value="">Projekt auswählen</option>
            <option v-for="p in allProjects" :key="p.id" :value="p.id">
              {{ p.Titel || p.titel || `Projekt #${p.id}` }}
            </option>
          </select>
        </div>
        <div class="flex items-center gap-2">
          <input id="mergeCopyData" class="small" v-model="mergeCopyData" type="checkbox" />
          <label for="mergeCopyData" class=" text-sm text-gray-700">Angaben von Quell- auf Zielprojekt übernehmen</label>
        </div>
        <div class="flex justify-end gap-2 pt-2">
          <button @click="closeMergeModal" class="px-4 py-2 border rounded hover:bg-gray-50">Abbrechen</button>
          <button
            @click="mergeProject"
            :disabled="mergeLoading || !mergeTargetId"
            class="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50"
          >
            {{ mergeLoading ? 'Führe zusammen...' : 'Projekte zusammenführen' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div v-if="loading" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6">
        <div class="text-lg font-semibold">Lade...</div>
      </div>
    </div>
  </div>
</template>

<script>
import API from '@/API'

const PROJECT_TYPES = Object.freeze([
  { value: '', label: 'Bitte auswählen' },
  { value: 'OGS/SEK', label: 'OGS/SEK' },
  { value: 'Projekt', label: 'Projekt' }
])

const DATE_FIELDS = Object.freeze([
  'StartDatum',
  'EndDatum',
  'Kuendigungsfrist1',
  'Kuendigungsfrist2',
  'Kuendigungsfrist3',
  'Kuendigungsfrist4',
  'Kuendigungsfrist5',
  'Kuendigungsfrist6',
  'Kuendigungsdatum1',
  'Kuendigungsdatum2',
  'Kuendigungsdatum3',
  'Kuendigungsdatum4',
  'Kuendigungsdatum5',
  'Kuendigungsdatum6'
])

const DATETIME_FIELDS = Object.freeze(['Freischaltung', 'BuchbarBis'])

const BASE_FIELDS = Object.freeze([
  { key: 'Titel', label: 'Titel', type: 'text', colSpan: 'md:col-span-2' },
  { key: 'Projektart', label: 'Projektart', type: 'select', options: PROJECT_TYPES },
  { key: 'Archiviert', label: 'Archiviert', type: 'checkbox' },
  { key: 'EinmaligesProjekt', label: 'Einmaliges Projekt', type: 'checkbox' },
  { key: 'Buchbar', label: 'Buchbar', type: 'checkbox' },
  { key: 'WartelisteAktiv', label: 'Warteliste aktiv', type: 'checkbox' },
  { key: 'NurVormerkungen', label: 'Nur Vormerkungen', type: 'checkbox' },
  { key: 'Reihenfolge', label: 'Position in Buchungsansicht', type: 'number' },
  { key: 'Freischaltung', label: 'Freischaltung', type: 'datetime-local' },
  { key: 'BuchbarBis', label: 'Buchbar bis', type: 'datetime-local' }
])

const SECTIONS = Object.freeze([
  {
    id: 'adresse',
    title: 'Adresse & Laufzeit',
    grid: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: 'Ort', label: 'Ort', type: 'text' },
      { key: 'Strasse', label: 'Straße', type: 'text' },
      { key: 'Hausnummer', label: 'Hausnummer', type: 'text' },
      { key: 'PLZ', label: 'PLZ', type: 'text' },
      { key: 'Stadt', label: 'Stadt', type: 'text' },
      { key: 'StartDatum', label: 'Startdatum', type: 'date' },
      { key: 'EndDatum', label: 'Enddatum', type: 'date' }
    ]
  },
  {
    id: 'kosten',
    title: 'Kosten & Abrechnung',
    grid: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: 'Kosten', label: 'Kosten', type: 'text' },
      { key: 'Anzahlung', label: 'Anzahlung', type: 'text' },
      { key: 'ButKosten', label: 'BuT Kosten', type: 'text' },
      { key: 'Gegenkonto', label: 'Gegenkonto', type: 'text' },
      { key: 'Kostenstelle', label: 'Kostenstelle', type: 'text' },
      { key: 'LetzteFibuNummer', label: 'Letzte Fibu Nummer', type: 'text' },
      { key: 'Verantwortlicher', label: 'Verantwortlicher', type: 'text' },
      { key: 'EmailProjekt', label: 'E-Mail Projekt', type: 'email' }
    ]
  },
  {
    id: 'beschreibung',
    title: 'Beschreibung',
    grid: 'grid-cols-1',
    fields: [
      { key: 'Beschreibung', label: 'Beschreibung', type: 'textarea', rows: 5 }
    ]
  },
  {
    id: 'buchungsoptionen',
    title: 'Buchungsoptionen',
    grid: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: 'Tagesauswahl', label: 'Tagesauswahl', type: 'checkbox' },
      { key: 'TageAenderbar', label: 'Tage änderbar', type: 'checkbox' },
      {
        key: 'Betreuungsendeoptionen',
        label: 'Betreuungsendeoptionen',
        type: 'betreuungsende',
        help: 'Kommaseparierte Liste im Format HH:MM. Keine Zeit angegeben = Keine auswählbar'
      },
      { key: 'AbholzeitenProWochentag', label: 'Abholzeiten pro Wochentag', type: 'checkbox' },
      { key: 'DienstagAusblenden', label: 'Dienstag ausblenden', type: 'checkbox' },
      { key: 'FreitagAusblenden', label: 'Freitag ausblenden', type: 'checkbox' },
      { key: 'Essensangaben', label: 'Essensangaben', type: 'checkbox' },
      { key: 'Unteraccounts', label: 'Unteraccounts', type: 'checkbox' },
      { key: 'SchulischeAngaben', label: 'Schulische Angaben', type: 'checkbox' },
      { key: 'IndividuelleLaufzeit', label: 'Individuelle Laufzeit', type: 'checkbox' },
      { key: 'ZustimmungFoto', label: 'Zustimmung Foto', type: 'checkbox' },
      { key: 'EhrenamtsSelfservice', label: 'Ehrenamt-Selfservice', type: 'checkbox' },
      { key: 'AusweisAbfragen', label: 'Ausweis abfragen', type: 'checkbox' },
      { key: 'BehinderungAbfragen', label: 'Behinderung abfragen', type: 'checkbox' },
      { key: 'GeburtsortAbfragen', label: 'Geburtsort abfragen', type: 'checkbox' },
      { key: 'BUTAbfrage', label: 'BuT Abfrage', type: 'checkbox' },
      { key: 'BUTAuswahl', label: 'BuT Auswahl', type: 'checkbox' }
    ]
  },
  {
    id: 'abholung',
    title: 'Abholung',
    grid: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: 'Abholausnahmen', label: 'Abholausnahmen', type: 'checkbox' },
      { key: 'AbholausnahmenMail', label: 'Abholausnahmen Mail', type: 'text' },
      { key: 'AbholausnahmenTelefon', label: 'Abholausnahmen Telefon', type: 'text' },
      { key: 'AbholungNotwendigAbfragen', label: 'Abholung notwendig abfragen', type: 'checkbox' },
      { key: 'AnwesenheitstoolIntern', label: 'Anwesenheitstool intern', type: 'checkbox' },
    ]
  },
  {
    id: 'sondertage',
    title: 'Sondertage',
    grid: 'grid-cols-1',
    fields: []
  },
  {
    id: 'preise',
    title: 'Preisstaffelung',
    grid: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: 'Preisauswahl', label: 'Preisauswahl aktiv', type: 'checkbox' },
      { key: 'Preisauswahl1', label: 'Preisauswahl 1', type: 'text' },
      { key: 'Preisauswahl2', label: 'Preisauswahl 2', type: 'text' },
      { key: 'Preisauswahl3', label: 'Preisauswahl 3', type: 'text' },
      { key: 'Preisauswahl4', label: 'Preisauswahl 4', type: 'text' },
      { key: 'Preisauswahl5', label: 'Preisauswahl 5', type: 'text' }
    ]
  },
  {
    id: 'freifeld',
    title: 'Freifeld',
    grid: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: 'FreifeldAnzeigen', label: 'Freifeld anzeigen', type: 'checkbox' },
      { key: 'FreifeldTitel', label: 'Freifeld Titel', type: 'text' },
      { key: 'Freifeldoption1', label: 'Freifeldoption 1', type: 'text' },
      { key: 'Freifeldoption2', label: 'Freifeldoption 2', type: 'text' },
      { key: 'Freifeldoption3', label: 'Freifeldoption 3', type: 'text' },
      { key: 'Freifeldoption4', label: 'Freifeldoption 4', type: 'text' },
      { key: 'Freifeldoption5', label: 'Freifeldoption 5', type: 'text' }
    ]
  },
  {
    id: 'templates',
    title: 'Kommunikation & Vorlagen',
    grid: 'grid-cols-1',
    fields: [
      {
        key: 'Buchungshinweis',
        label: 'Buchungshinweis',
        type: 'textarea',
        placeholder: 'Buchungshinweis, der vor der Buchung als Popup angezeigt wird',
        rows: 4
      },
      { key: 'TemplateVormerkung', label: 'Template Vormerkung', type: 'textarea', rows: 4 },
      { key: 'TemplateEingang', label: 'Template Eingang', type: 'textarea', rows: 4 },
      { key: 'TemplateBestaetigung', label: 'Template Bestätigung', type: 'textarea', rows: 4 },
      { key: 'TemplateAbsage', label: 'Template Absage', type: 'textarea', rows: 4 },
      { key: 'TemplateKuendigungseingang', label: 'Template Kündigungseingang', type: 'textarea', rows: 4 },
      { key: 'TemplateKuendigungsbestaetigung', label: 'Template Kündigungsbestätigung', type: 'textarea', rows: 4 },
      { key: 'TemplateWarteliste', label: 'Template Warteliste', type: 'textarea', rows: 4 },
      { key: 'TemplateEingangWiderruf', label: 'Template Eingang Widerruf', type: 'textarea', rows: 4 },
      { key: 'TemplateBestaetigungWiderruf', label: 'Template Bestätigung Widerruf', type: 'textarea', rows: 4 },
      {
        key: 'TemplateAbholungsanfrage',
        label: 'Template Abholungsanfrage',
        type: 'textarea',
        rows: 4,
        help: 'Platzhalter: %VORNAME%, %NACHNAME%, %VORNAMEKIND%, %NACHNAMEKIND%, %DATUM%, %ERGEBNIS%, %GRUND%'
      }
    ]
  },
  {
    id: 'kuendigung',
    title: 'Kündigungsbedingungen',
    grid: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: 'KuendigungMoeglich', label: 'Kündigung möglich', type: 'checkbox' },
      { key: 'Kuendigungsfrist1', label: 'Kündigungsfrist 1', type: 'date' },
      { key: 'Kuendigungsdatum1', label: 'Kündigungsdatum 1', type: 'date' },
      { key: 'Kuendigungskosten1', label: 'Kündigungskosten 1', type: 'text' },
      { key: 'Kuendigungsfrist2', label: 'Kündigungsfrist 2', type: 'date' },
      { key: 'Kuendigungsdatum2', label: 'Kündigungsdatum 2', type: 'date' },
      { key: 'Kuendigungskosten2', label: 'Kündigungskosten 2', type: 'text' },
      { key: 'Kuendigungsfrist3', label: 'Kündigungsfrist 3', type: 'date' },
      { key: 'Kuendigungsdatum3', label: 'Kündigungsdatum 3', type: 'date' },
      { key: 'Kuendigungskosten3', label: 'Kündigungskosten 3', type: 'text' },
      { key: 'Kuendigungsfrist4', label: 'Kündigungsfrist 4', type: 'date' },
      { key: 'Kuendigungsdatum4', label: 'Kündigungsdatum 4', type: 'date' },
      { key: 'Kuendigungskosten4', label: 'Kündigungskosten 4', type: 'text' },
      { key: 'Kuendigungsfrist5', label: 'Kündigungsfrist 5', type: 'date' },
      { key: 'Kuendigungsdatum5', label: 'Kündigungsdatum 5', type: 'date' },
      { key: 'Kuendigungskosten5', label: 'Kündigungskosten 5', type: 'text' },
      { key: 'Kuendigungsfrist6', label: 'Kündigungsfrist 6', type: 'date' },
      { key: 'Kuendigungsdatum6', label: 'Kündigungsdatum 6', type: 'date' },
      { key: 'Kuendigungskosten6', label: 'Kündigungskosten 6', type: 'text' }
    ]
  },
  {
    id: 'upload',
    title: 'Upload-Dokumente',
    grid: 'grid-cols-1 md:grid-cols-2',
    fields: [
      { key: 'UploadDokument1', label: 'Upload Dokument 1', type: 'text' },
      { key: 'UploadDokument2', label: 'Upload Dokument 2', type: 'text' },
      { key: 'UploadDokument3', label: 'Upload Dokument 3', type: 'text' },
      { key: 'UploadDokument4', label: 'Upload Dokument 4', type: 'text' },
      { key: 'UploadDokument5', label: 'Upload Dokument 5', type: 'text' }
    ]
  }
])

const PERMISSION_OPTIONS = Object.freeze([
  { value: 0, label: 'Kein Zugriff' },
  { value: 1, label: 'Lesen' },
  { value: 2, label: 'Hinzufügen' },
  { value: 3, label: 'Ändern' }
])

const EMPTY_PROJECT = () => ({
  Titel: '',
  Archiviert: false,
  Projektart: '',
  Ort: '',
  Strasse: '',
  Hausnummer: '',
  PLZ: '',
  Stadt: '',
  EinmaligesProjekt: false,
  Buchbar: false,
  WartelisteAktiv: false,
  NurVormerkungen: false,
  Reihenfolge: '',
  Freischaltung: '',
  BuchbarBis: '',
  Kosten: '',
  Anzahlung: '',
  ButKosten: '',
  StartDatum: '',
  EndDatum: '',
  Verantwortlicher: '',
  EmailProjekt: '',
  Kostenstelle: '',
  LetzteFibuNummer: '',
  Beschreibung: '',
  Tagesauswahl: false,
  TageAenderbar: false,
  Betreuungsendeoptionen: '',
  DienstagAusblenden: false,
  FreitagAusblenden: false,
  Essensangaben: false,
  Unteraccounts: false,
  AbholungNotwendigAbfragen: false,
  AnwesenheitstoolIntern: false,
  Abholausnahmen: false,
  AbholausnahmenMail: '',
  AbholausnahmenTelefon: '',
  AbholzeitenProWochentag: false,
  SchulischeAngaben: false,
  IndividuelleLaufzeit: false,
  ZustimmungFoto: false,
  EhrenamtsSelfservice: true,
  AusweisAbfragen: false,
  BehinderungAbfragen: false,
  GeburtsortAbfragen: false,
  BUTAbfrage: false,
  BUTAuswahl: false,
  Preisauswahl: false,
  Preisauswahl1: '',
  Preisauswahl2: '',
  Preisauswahl3: '',
  Preisauswahl4: '',
  Preisauswahl5: '',
  FreifeldAnzeigen: false,
  FreifeldTitel: '',
  Freifeldoption1: '',
  Freifeldoption2: '',
  Freifeldoption3: '',
  Freifeldoption4: '',
  Freifeldoption5: '',
  TemplateVormerkung: '',
  TemplateEingang: '',
  TemplateBestaetigung: '',
  TemplateAbsage: '',
  TemplateKuendigungseingang: '',
  TemplateKuendigungsbestaetigung: '',
  TemplateWarteliste: '',
  TemplateEingangWiderruf: '',
  TemplateBestaetigungWiderruf: '',
  TemplateAbholungsanfrage: '',
  Gegenkonto: '',
  KuendigungMoeglich: false,
  Kuendigungsfrist1: '',
  Kuendigungsfrist2: '',
  Kuendigungsfrist3: '',
  Kuendigungsfrist4: '',
  Kuendigungsfrist5: '',
  Kuendigungsfrist6: '',
  Kuendigungsdatum1: '',
  Kuendigungsdatum2: '',
  Kuendigungsdatum3: '',
  Kuendigungsdatum4: '',
  Kuendigungsdatum5: '',
  Kuendigungsdatum6: '',
  Kuendigungskosten1: '',
  Kuendigungskosten2: '',
  Kuendigungskosten3: '',
  Kuendigungskosten4: '',
  Kuendigungskosten5: '',
  Kuendigungskosten6: '',
  Buchungshinweis: '',
  UploadDokument1: '',
  UploadDokument2: '',
  UploadDokument3: '',
  UploadDokument4: '',
  UploadDokument5: ''
})

export default {
  name: 'ProjectEdit',
  props: {
    user: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      loading: false,
      saving: false,
      projectId: null,
      copyMode: false,
      project: EMPTY_PROJECT(),
      originalProject: {},
      projectStats: null,
      files: [],
      restrictedViews: [],
      availableUsers: [],
      newPermission: {
        userId: '',
        permission: 1
      },
      currentUser: null,
      baseFields: BASE_FIELDS,
      sections: SECTIONS,
      permissionOptions: PERMISSION_OPTIONS,
      mergeModalOpen: false,
      mergeTargetId: '',
      mergeCopyData: false,
      allProjects: [],
      mergeLoading: false,
      participationRequests: [],
      betreuungsendeInput: '',
      sondertage: [],
      sondertagecopied: false,
      quickNavOpen: false,
      collapsedSections: {
        projekt: false,
        dokumente: false,
        ehrenamt: false,
        berechtigungen: false,
        ...Object.fromEntries(SECTIONS.map(s => [s.id, false]))
      }
    }
  },
  computed: {
    headerTitle() {
      if (this.copyMode) {
        return 'Projekt kopieren'
      }
      if (!this.projectId) {
        return 'Neues Projekt anlegen'
      }
      return `Projekt #${this.projectId} bearbeiten`
    },
    resolvedUser() {
      return this.currentUser || this.user
    },
    showProjectActions() {
      return this.projectId && this.resolvedUser && this.resolvedUser.Projects > 0
    },
    showSaveButtons() {
      if (!this.resolvedUser) return false
      if (this.projectId) {
        return this.canEditProject
      }
      return this.resolvedUser.Projects >= 2
    },
    canEditProject() {
      if (!this.resolvedUser || !this.projectId) return false
      if (this.resolvedUser.Projects >= 3) return true
      const restrictedViews = this.resolvedUser.restrictedViews || {}
      const restrictedLevel =
        restrictedViews[this.projectId] ?? restrictedViews[String(this.projectId)]
      return restrictedLevel >= 3
    },
    canViewPermissions() {
      return this.resolvedUser && this.resolvedUser.Projects > 0
    },
    canEditPermissions() {
      return this.resolvedUser && this.resolvedUser.Permissions > 0
    },
    canUploadFiles() {
      return this.resolvedUser && this.resolvedUser.UploadProjectFiles > 0
    },
    contractsUrl() {
      if (!this.projectId) return '#'
      return `/contracts?Projekt=${this.projectId}`
    },
    baseOrigin() {
      try {
        const envUrl = process.env.VUE_APP_INTERNAL_URL
        if (envUrl) {
          return new URL(envUrl).origin
        }
      } catch (e) {}
      return new URL(window.location.href).origin
    },
    betreuungsendePills() {
      if (!this.project.Betreuungsendeoptionen) return []
      return this.project.Betreuungsendeoptionen.split(',').map(s => s.trim()).filter(Boolean)
    },
    sondertageLink() {
      const meinUrl = process.env.VUE_APP_MEIN_URL || 'https://mein.ev-angel-isch.de'
      return `${meinUrl}/services?sondertage=${this.projectId}`
    },
    navSections() {
      const items = [{ id: 'projekt', label: 'Projekt', icon: 'edit' }]
      this.sections.forEach(s => items.push({ id: s.id, label: s.title, icon: 'list' }))
      if (this.projectId && !this.copyMode) {
        items.push({ id: 'dokumente', label: 'Dokumente', icon: 'file' })
        if (this.project.EhrenamtsSelfservice) {
          items.push({ id: 'ehrenamt', label: 'Ehrenamt Teilnahmeanfragen', icon: 'user' })
        }
        if (this.canViewPermissions) {
          items.push({ id: 'berechtigungen', label: 'Berechtigungen', icon: 'key' })
        }
      }
      return items
    }
  },
  async created() {
    const idParam = this.$route.params.id
    const copyId = this.$route.query.copyId
    if (!this.user) {
      await this.fetchUserData()
    }

    if (copyId) {
      this.copyMode = true
      await this.loadProject(parseInt(copyId), true)
    } else if (idParam && idParam !== 'new') {
      this.projectId = parseInt(idParam)
      await this.loadProject(this.projectId, false)
    }
  },
  methods: {
    toggleSection(section) {
      this.collapsedSections[section] = !this.collapsedSections[section]
    },
    scrollToSection(refName) {
      if (refName in this.collapsedSections) {
        this.collapsedSections[refName] = false
      }
      this.$nextTick(() => {
        const ref = this.$refs['section-' + refName]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
      this.quickNavOpen = false
    },
    async fetchUserData() {
      try {
        const response = await new Promise((resolve, reject) => {
          API.checkAuth((data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to fetch user data'))
            }
          })
        })
        this.currentUser = response
      } catch (error) {
        console.error('Error fetching user data:', error)
        window.location.href = `${process.env.VUE_APP_LOGIN_URL}?next=${window.location.href}`
      }
    },
    async loadProject(projectId, isCopy) {
      this.loading = true
      try {
        const response = await new Promise((resolve, reject) => {
          API.getProject(projectId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to load project'))
            }
          })
        })

        const projectData = this.convertDatesFromBackend(response.project || {})
        if (isCopy) {
          this.projectId = null
          this.copyMode = true
          const titlePrefix = projectData.Titel ? `KOPIE: ${projectData.Titel}` : 'KOPIE: '
          this.project = { ...this.project, ...projectData, Titel: titlePrefix }
          this.projectStats = null
          this.files = []
          this.restrictedViews = []
        } else {
          this.projectId = projectData.id || projectId
          this.project = { ...this.project, ...projectData }
          this.projectStats = response.projectStats || null
          this.files = response.files || []
          this.restrictedViews = response.restrictedViews || []
        }
        this.availableUsers = response.availableUsers || []
        this.originalProject = this._cloneProjectPlain(this.project)
        if (!isCopy && this.projectId) {
          await this.loadParticipationRequests()
          await this.loadSondertage()
        }
      } catch (error) {
        console.error('Error loading project:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Laden des Projekts',
          type: 'error',
          position: 'top'
        })
      } finally {
        this.loading = false
      }
    },
    convertDatesFromBackend(projectData) {
      const converted = { ...projectData }
      DATE_FIELDS.forEach((field) => {
        if (converted[field]) {
          converted[field] = converted[field].split('T')[0]
        } else {
          converted[field] = ''
        }
      })
      DATETIME_FIELDS.forEach((field) => {
        if (converted[field]) {
          try {
            const date = new Date(converted[field])
            const offset = date.getTimezoneOffset()
            const localDate = new Date(date.getTime() - offset * 60000)
            converted[field] = localDate.toISOString().slice(0, 16)
          } catch (e) {
            converted[field] = ''
          }
        } else {
          converted[field] = ''
        }
      })
      return converted
    },
    preparePayload() {
      const payload = { ...this.project }
      DATE_FIELDS.forEach((field) => {
        if (payload[field] === '') {
          payload[field] = null
        }
      })
      DATETIME_FIELDS.forEach((field) => {
        if (!payload[field]) {
          payload[field] = null
        } else {
          payload[field] = new Date(payload[field]).toISOString()
        }
      })
      delete payload.id
      return payload
    },
    async saveProject() {
      this.saving = true
      try {
        const payload = this.preparePayload()
        const response = await new Promise((resolve, reject) => {
          if (this.projectId) {
            API.updateProject(this.projectId, payload, (data, status) => {
              if (status === 200) {
                resolve(data)
              } else {
                reject(new Error('Failed to update project'))
              }
            })
          } else {
            API.createProject(payload, (data, status) => {
              if (status === 201) {
                resolve(data)
              } else {
                reject(new Error('Failed to create project'))
              }
            })
          }
        })

        if (!this.projectId) {
          this.copyMode = false
          this.projectId = response.id
          this.$router.replace({ path: `/projects/${response.id}` })
          await this.loadProject(response.id, false)
        } else {
          const updated = this.convertDatesFromBackend(response)
          this.project = { ...this.project, ...updated }
          this.originalProject = this._cloneProjectPlain(this.project)
          await this.refreshSupplemental()
        }

        this.$toast?.open?.({
          message: 'Projekt gespeichert',
          type: 'success',
          position: 'top'
        })
        return true
      } catch (error) {
        console.error('Error saving project:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Speichern des Projekts',
          type: 'error',
          position: 'top'
        })
        return false
      } finally {
        this.saving = false
      }
    },
    async saveAndClose() {
      if (await this.saveProject()) {
        this.goBack()
      }
    },
    goBack() {
      const referrer = document.cookie.split('; ').find((row) => row.startsWith('tableReferrer='))
      if (referrer) {
        const url = referrer.split('=')[1]
        window.location.href = decodeURIComponent(url)
      } else {
        this.$router.push('/projects')
      }
    },
    copyProject() {
      if (!this.projectId) return
      window.location.href = `/projects/new?copyId=${this.projectId}`
    },
    openMergeModal() {
      if (!this.projectId) return
      this.mergeModalOpen = true
      this.loadAllProjects()
    },
    closeMergeModal() {
      this.mergeModalOpen = false
      this.mergeTargetId = ''
      this.mergeCopyData = false
    },
    async loadAllProjects() {
      try {
        const response = await new Promise((resolve, reject) => {
          API.getAllProjects((data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to load projects'))
            }
          })
        })
        this.allProjects = (response || []).filter(p => p.id !== this.projectId)
      } catch (error) {
        console.error('Error loading projects:', error)
      }
    },
    async mergeProject() {
      if (!this.projectId || !this.mergeTargetId) return
      if (parseInt(this.mergeTargetId) === this.projectId) {
        this.$toast?.open?.({ message: 'Quell- und Zielprojekt müssen unterschiedlich sein', type: 'error', position: 'top' })
        return
      }
      this.mergeLoading = true
      try {
        await new Promise((resolve, reject) => {
          API.mergeProject(this.projectId, {
            target: parseInt(this.mergeTargetId),
            copyData: this.mergeCopyData
          }, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to merge projects'))
            }
          })
        })
        this.$toast?.open?.({ message: 'Projekte wurden zusammengeführt', type: 'success', position: 'top' })
        this.closeMergeModal()
        this.goBack()
      } catch (error) {
        console.error('Error merging projects:', error)
        this.$toast?.open?.({ message: 'Fehler beim Zusammenführen der Projekte', type: 'error', position: 'top' })
      } finally {
        this.mergeLoading = false
      }
    },
    openFile(file) {
      if (!file?.downloadUrl) return
      window.open(`${this.baseOrigin}${file.downloadUrl}`, '_blank')
    },
    deleteFile(file) {
      if (!this.canUploadFiles) return
      if (!confirm('Möchten Sie diese Datei wirklich löschen?')) return
      window.open(`${this.baseOrigin}/deleteProjectFile?fileID=${file.id}`, '_blank')
      setTimeout(() => {
        this.refreshSupplemental()
      }, 1200)
    },
    uploadDocument() {
      if (!this.projectId) return
      window.open(`${this.baseOrigin}/uploadProject?id=${this.projectId}`, '_blank')
    },
    async refreshSupplemental() {
      if (!this.projectId) return
      try {
        const response = await new Promise((resolve, reject) => {
          API.getProject(this.projectId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to refresh project'))
            }
          })
        })
        this.projectStats = response.projectStats || this.projectStats
        this.files = response.files || []
        this.restrictedViews = response.restrictedViews || this.restrictedViews
        this.availableUsers = response.availableUsers || this.availableUsers
      } catch (error) {
        console.error('Error refreshing project data:', error)
      }
    },
    async savePermission(row, isNew = false) {
      if (!this.canEditPermissions) {
        this.$toast?.open?.({
          message: 'Keine Berechtigung zum Ändern der Zugriffe',
          type: 'error',
          position: 'top'
        })
        return
      }
      if (!this.projectId) return
      if (!row.userId) {
        this.$toast?.open?.({
          message: 'Bitte eine Mailadresse auswählen',
          type: 'error',
          position: 'top'
        })
        return
      }
      try {
        const response = await new Promise((resolve, reject) => {
          API.setProjectPermission(
            this.projectId,
            { user: row.userId, permission: row.permission },
            (data, status) => {
              if (status === 200) {
                resolve(data)
              } else {
                reject(new Error('Failed to update permission'))
              }
            }
          )
        })

        if (response.status === 'deleted') {
          this.restrictedViews = this.restrictedViews.filter((item) => item.userId !== row.userId)
        } else if (response.id) {
          const existingIndex = this.restrictedViews.findIndex((item) => item.id === response.id)
          if (existingIndex >= 0) {
            this.restrictedViews.splice(existingIndex, 1, response)
          } else {
            this.restrictedViews.push(response)
          }
        }

        if (isNew) {
          this.newPermission = { userId: '', permission: 1 }
        }

        this.$toast?.open?.({
          message: 'Berechtigung gespeichert',
          type: 'success',
          position: 'top'
        })
      } catch (error) {
        console.error('Error saving permission:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Speichern der Berechtigung',
          type: 'error',
          position: 'top'
        })
      }
    },
    _cloneProjectPlain(project) {
      const clone = {}
      const keys = Object.keys(EMPTY_PROJECT())
      for (let i = 0; i < keys.length; i++) {
        const key = keys[i]
        clone[key] = project[key]
      }
      return clone
    },
    formatDateTime(value) {
      if (!value) return ''
      const date = new Date(value)
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    async loadParticipationRequests() {
      if (!this.projectId) return
      try {
        const response = await new Promise((resolve, reject) => {
          API.getProjectParticipationRequests(this.projectId, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to load participation requests'))
            }
          })
        })
        this.participationRequests = response || []
      } catch (error) {
        console.error('Error loading participation requests:', error)
      }
    },
    async saveParticipationStatus(req, editMail = false) {
      try {
        const response = await new Promise((resolve, reject) => {
          API.updateParticipationRequestStatus(req.id, { status: req.status, compensation: req.compensation, editMail }, (data, status) => {
            if (status === 200) {
              resolve(data)
            } else {
              reject(new Error('Failed to update participation status'))
            }
          })
        })

        // If mailto link is returned, open it in new window
        if (response && response.mailto) {
          window.open(response.mailto, '_blank')
        }

        this.$toast?.open?.({
          message: response.message || 'Status gespeichert',
          type: 'success',
          position: 'top'
        })
      } catch (error) {
        console.error('Error saving participation status:', error)
        this.$toast?.open?.({
          message: 'Fehler beim Speichern des Status',
          type: 'error',
          position: 'top'
        })
      }
    },
    addBetreuungsende() {
      const val = this.betreuungsendeInput.trim()
      if (!val) return
      const parts = this.betreuungsendePills.slice()
      if (!parts.includes(val)) parts.push(val)
      this.project.Betreuungsendeoptionen = parts.join(',')
      this.betreuungsendeInput = ''
    },
    removeBetreuungsende(index) {
      const parts = this.betreuungsendePills.slice()
      parts.splice(index, 1)
      this.project.Betreuungsendeoptionen = parts.join(',')
    },
    async loadSondertage() {
      if (!this.projectId) return
      API.getSondertage(this.projectId, (data, status) => {
        if (status === 200) {
          this.sondertage = data.map(st => ({ ...st, saved: true }))
        }
      })
    },
    addSondertag() {
      this.sondertage.push({ label: '', startDate: '', endDate: '', default: 'abwesend', isNew: true, saved: false })
    },
    saveSondertag(st, index) {
      if (!st.label || !st.startDate) return
      if (st.isNew || !st.id) {
        API.createSondertag(this.projectId, { label: st.label, startDate: st.startDate, endDate: st.endDate || null, default: st.default, editStart: st.editStart || null, editEnd: st.editEnd || null }, (data, status) => {
          if (status === 201 || status === 200) { Object.assign(st, data); delete st.isNew; st.saved = true }
        })
      } else {
        API.updateSondertag(this.projectId, st.id, { label: st.label, startDate: st.startDate, endDate: st.endDate || null, default: st.default, editStart: st.editStart || null, editEnd: st.editEnd || null }, (data, status) => {
          if (status === 200) st.saved = true
        })
      }
    },
    removeSondertag(st, index) {
      if (!confirm('Sondertag wirklich löschen?')) return
      if (!st.id) { this.sondertage.splice(index, 1); return }
      API.deleteSondertag(this.projectId, st.id, (data, status) => {
        if (status === 200) this.sondertage.splice(index, 1)
      })
    },
    async copySondertageLink() {
      await navigator.clipboard.writeText(this.sondertageLink)
      this.sondertagecopied = true
      setTimeout(() => { this.sondertagecopied = false }, 2000)
    }
  }
}
</script>

<style scoped>
input[type="text"]:focus,
input[type="email"]:focus,
input[type="date"]:focus,
input[type="datetime-local"]:focus,
input[type="number"]:focus,
select:focus,
textarea:focus {
  outline: none;
  --tw-ring-offset-width: 0px;
  --tw-ring-offset-color: #fff;
  --tw-ring-color: rgb(59 130 246);
  --tw-ring-offset-shadow: 0 0 #0000;
  --tw-ring-shadow: 0 0 #0000;
  box-shadow: 0 0 0 2px var(--tw-ring-color);
}

textarea {
  min-height: 80px;
  max-height: 300px;
  overflow-y: auto;
}

@media (max-width: 1023px) {
  input[type="date"]::-webkit-calendar-picker-indicator,
  input[type="datetime-local"]::-webkit-calendar-picker-indicator {
    display: none;
  }
  input[type="date"],
  input[type="datetime-local"] {
    -webkit-appearance: none;
    -moz-appearance: textfield;
    padding-left: 4px;
    padding-right: 4px;
  }
}
</style>
